/**
 * Overlay Components
 * Loading, progress, and count-in overlay components
 * Extracted from DAWWorkspace.jsx for better modularity
 */

import { Loader2 } from 'lucide-react';
import { Progress } from '../../ui/progress';

// Count-In Overlay
export const CountInOverlay = ({ isCountingIn, countInProgress, countInBeats }) => {
  if (!isCountingIn) return null;
  
  return (
    <div className="absolute inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center">
      <div className="text-center">
        <div className="text-8xl font-bold text-indigo-500 mb-4 animate-pulse">
          {countInProgress}
        </div>
        <p className="text-xl text-zinc-400">Count-in...</p>
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: countInBeats }, (_, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition-all ${
                countInProgress > i
                  ? 'bg-indigo-500 scale-125'
                  : 'bg-zinc-700'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// Export Progress Overlay
export const ExportProgressOverlay = ({ exportProgress }) => {
  if (!exportProgress) return null;
  
  return (
    <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
      <div className="text-center w-64">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
        <p className="text-lg font-medium mb-2 capitalize">{exportProgress.stage}...</p>
        <Progress value={exportProgress.percent} className="h-2 mb-2" />
        <p className="text-sm text-zinc-400">{Math.round(exportProgress.percent)}%</p>
      </div>
    </div>
  );
};

// Loading Overlay
export const LoadingOverlay = ({ isLoading, message = 'Loading project...' }) => {
  if (!isLoading) return null;
  
  return (
    <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
        <p className="text-lg font-medium">{message}</p>
      </div>
    </div>
  );
};

// Save Progress Overlay
export const SaveProgressOverlay = ({ saveProgress }) => {
  if (!saveProgress) return null;
  
  const percent = (saveProgress.current / saveProgress.total) * 100;
  
  return (
    <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center">
      <div className="text-center w-64">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
        <p className="text-lg font-medium mb-2">Saving audio...</p>
        <Progress value={percent} className="h-2 mb-2" />
        <p className="text-sm text-zinc-400">{saveProgress.current} / {saveProgress.total} chunks</p>
      </div>
    </div>
  );
};

// Combined Overlays Component
export const DAWOverlays = ({
  isCountingIn,
  countInProgress,
  countInBeats,
  exportProgress,
  isLoading,
  saveProgress,
}) => {
  return (
    <>
      <CountInOverlay 
        isCountingIn={isCountingIn} 
        countInProgress={countInProgress} 
        countInBeats={countInBeats} 
      />
      <ExportProgressOverlay exportProgress={exportProgress} />
      <LoadingOverlay isLoading={isLoading} />
      <SaveProgressOverlay saveProgress={saveProgress} />
    </>
  );
};

export default DAWOverlays;
